import React, { useState, useEffect, useRef } from 'react';

// --- Utility Functions ---
const DEFAULT_COST_ALT_TEXT = 'This is an estimate—factors like where you live could impact your cost. For multiple items, estimated cost can vary based on type(s) of jewelry.';

function calculateMonthlyCost(yearlyCost) {
  return Math.round((yearlyCost / 12) * 100) / 100;
}

function getPolMinPremForState(zipData) {
  if (
    zipData &&
    zipData.rate &&
    zipData.rate.min_premium_override !== undefined &&
    zipData.rate.min_premium_override !== null &&
    !isNaN(Number(zipData.rate.min_premium_override)) &&
    Number(zipData.rate.min_premium_override) > 0
  ) {
    return Number(zipData.rate.min_premium_override);
  }
  return undefined;
}

const US_ZIP_REGEX = /^\d{5}$/;
const CA_POSTAL_REGEX = /(^[ABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Z]{1}[ -]?\d{1}[A-Z]{1}\d{1}$)/i;
const DEFAULT_ZIPS = ['54956', '37130', '08880', '75142'];

// Rejection sampling avoids the modulo bias of `randomUint32() % maxExclusive`
// by discarding draws outside the largest range evenly divisible by maxExclusive.
function secureRandomIndex(maxExclusive) {
  const uint32Range = 0x100000000;
  const limit = uint32Range - (uint32Range % maxExclusive);
  const array = new Uint32Array(1);
  let value;
  do {
    crypto.getRandomValues(array);
    value = array[0];
  } while (value >= limit);
  return value % maxExclusive;
}

function pickDefaultZip() {
  return DEFAULT_ZIPS[secureRandomIndex(DEFAULT_ZIPS.length)];
}

// Fallback for browsers without randomUUID().
function generateItemId() {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// --- MixPanel Tracking Utilities ---

/**
 * Builds the Quote Updated payload per Logan's spreadsheet specification.
 * Only includes properties marked as "Set" for Quick Quote.
 */
function buildQuoteUpdatedPayload({
  jewelryType,
  itemValue,
  zipCode,
  zipData,
  yearlyCost,
}) {
  // Determine item type and subtype based on jewelry selection
  const isEngagementRing = jewelryType === 'Engagement Ring';
  const itemType = isEngagementRing ? 'ring' : (jewelryType || '').toLowerCase();
  const itemSubtype = isEngagementRing ? 'ladies engagement' : '';
  const itemGender = isEngagementRing ? 'Female' : '';

  // Extract location data from zipData if available
  const city = zipData?.city || '';
  const state = zipData?.state || '';
  const country = zipData?.country || (US_ZIP_REGEX.test(zipCode) ? 'US' : (CA_POSTAL_REGEX.test(zipCode) ? 'CA' : ''));

  // Build the payload
  const payload = {
    // Always set to 1 for Quick Quote (single item)
    '# Items': 1,
    '# Wearers': 1,
    'Total Item Count': 1,

    // Location data
    'Zip/Postal Code': zipCode || '',
    ...(city && { 'City': city }),
    ...(state && { 'State/Province': state }),
    ...(country && { 'Country': country }),

    // Page info
    'Current Page Name': document.title || '',

    // Quote origination
    'Initial Quote Origination': 'QuickQuote',
    'Technology Source': 'QuickQuote',

    // Item details - arrays with single element for QQ
    'Item Types': [itemType],
    'Item Values': [itemValue ? itemValue.toString() : ''],
    'Item Subtypes': [itemSubtype],
    'Item Genders': [itemGender],
    'Item Deductibles': ['0.0'],
    'Item Appraisal Attached': [false],
    'Item Dates of Purchase or Last Appraisal': [''],
    'Item Diamond Types': [''],
    'Item Other Descriptions': [''],
    'Item Serial Numbers': [''],

    // Deductible (always 0 for QQ)
    'Current Deductible': ['0.0'],

    // Premium/cost data (only if available)
    ...(yearlyCost !== null && yearlyCost !== undefined && {
      'Current Premium': [yearlyCost],
      'Item Premiums': [yearlyCost],
      'Total Quoted Amount': yearlyCost,
    }),

    // Total value
    'Total Value of Items': itemValue || 0,

    // Item Details object array
    'Item Details': [{
      'Item Types': itemType,
      'Item Values': itemValue || 0,
      'Item Subtypes': itemSubtype,
      'Item Genders': itemGender,
      'Item Deductibles': 0,
      'Item Appraisal Attached': [],
      'Item Dates or Purchase of Last Appraisal': '',
      'Item Diamond Types': '',
      'Item Premiums': yearlyCost || 0,
      'Wearer Relationships': 'Applicant',
    }],
  };

  return payload;
}

function sendMixpanelTrack(instance, label, payload) {
  if (instance && typeof instance.track === 'function') {
    instance.track('Quote Updated', payload);
    console.debug(`MixPanel${label}: Quote Updated tracked`, payload);
  }
}

function trackQuoteUpdated(payload) {
  sendMixpanelTrack(typeof window.mixpanel !== 'undefined' ? window.mixpanel : null, '', payload);
}

function buildElementsChanged(previous, current) {
  if (!previous) return [];
  const changes = [];
  if (previous.zipCode !== current.zipCode) {
    changes.push({
      'Item Element': 'Applicant Zip/Postal Code',
      'Old Value': previous.zipCode ?? '',
      'New Value': current.zipCode ?? '',
    });
  }
  if (previous.jewelryType !== current.jewelryType) {
    changes.push({
      'Item Element': 'Item Types',
      'Old Value': previous.jewelryType ?? '',
      'New Value': current.jewelryType ?? '',
    });
  }
  if (previous.itemValue !== current.itemValue) {
    changes.push({
      'Item Element': 'Item Values',
      'Old Value': previous.itemValue ?? '',
      'New Value': current.itemValue ?? '',
    });
  }
  return changes;
}

// Premium fields only apply to Ring/Engagement Ring (the only ratable type).
function buildEnterpriseUnifiedQuoteUpdatedPayload({
  jewelryType,
  itemValue,
  zipCode,
  zipData,
  yearlyCost,
  itemId,
  previous,
}) {
  const isEngagementRing = jewelryType === 'Engagement Ring';
  const isRingType = isEngagementRing || jewelryType === 'Ring';
  const itemType = isEngagementRing ? 'ring' : (jewelryType || '').toLowerCase();
  const itemSubtype = isEngagementRing ? 'engagement' : '';
  const itemGender = isEngagementRing ? 'Female' : '';

  const city = zipData?.city || '';
  const state = zipData?.state || '';
  const country = zipData?.country || (US_ZIP_REGEX.test(zipCode) ? 'US' : (CA_POSTAL_REGEX.test(zipCode) ? 'CA' : ''));

  const hasPremium = isRingType && yearlyCost !== null && yearlyCost !== undefined;

  const itemDetails = {
    'Item Appraisal Attached': false,
    'Item Brand': '',
    'Item Damage': 'No',
    'Item Dates or Purchase of Last Appraisal': '',
    'Item Deductibles': 0,
    'Item Diamond Types': '',
    'Item Genders': itemGender,
    'Item ID': itemId,
    'Item Other Descriptions': '',
    'Item Possession': '',
    'Item Serial Number': '',
    'Item Subtypes': itemSubtype,
    'Item Types': itemType,
    'Item Values': itemValue || 0,
    ...(hasPremium && { 'Item Premiums': yearlyCost }),
  };

  return {
    '$PageTitle': document.title || '',
    'Account Number': '',
    'Acquisition Channel': 'Web',
    'Acquisition Referral': '',
    'Acquisition Source': '',
    'Acquisition Source Detail': '',
    'Applicant City': city,
    'Applicant Country': country,
    'Applicant County': '',
    'Applicant Credit Group': '',
    'Applicant State/Province': state,
    'Applicant Zip/Postal Code': zipCode || '',
    'Item Details': [itemDetails],
    'Item Elements Changed': buildElementsChanged(previous, { zipCode, jewelryType, itemValue }),
    'Product Code': '',
    'Quote ID': '',
    'Quote Status': 'Quick Quote',
    'Technology Source': 'QuickQuote',
    'Total Item Count': 1,
    'Total Item Value': itemValue || 0,
    ...(hasPremium && { 'Total Item Premium': yearlyCost }),
  };
}

function trackEnterpriseUnifiedQuoteUpdated(payload) {
  sendMixpanelTrack(typeof window.mixpanel !== 'undefined' ? window.mixpanel.autotrack : null, ' (Enterprise Unified)', payload);
}

// Fires both trackers for one changed result; shared by the ring and non-ring effects below.
function fireQuoteUpdatedTrackers({ jewelryType, itemValue, zipCode, zipData, yearlyCost, itemId, previous }) {
  trackQuoteUpdated(buildQuoteUpdatedPayload({ jewelryType, itemValue, zipCode, zipData, yearlyCost }));
  trackEnterpriseUnifiedQuoteUpdated(
    buildEnterpriseUnifiedQuoteUpdatedPayload({ jewelryType, itemValue, zipCode, zipData, yearlyCost, itemId, previous })
  );
}

// --- Components ---

const CitySelect = ({
  onZipChange,
  error,
  setError,
  setIsZipLoading,
  selectedZip,
}) => {
  const [query, setQuery] = useState(selectedZip || '');
  const pendingZipDataRef = useRef(null);

  useEffect(() => {
    if (selectedZip !== undefined && selectedZip !== query) {
      setQuery(selectedZip);
    }
  }, [selectedZip]);

  const handleZipChange = (zip) => {
    setIsZipLoading(true);
    let displayZip = zip;
    let apiZip = zip.trim();
    setError('');
    localStorage.setItem('jm-qq-zip', '');
    if (US_ZIP_REGEX.test(apiZip) || CA_POSTAL_REGEX.test(apiZip)) {
      if (CA_POSTAL_REGEX.test(apiZip)) {
        const compact = apiZip.toUpperCase().replace(/[^A-Z0-9]/g, '');
        displayZip = compact.replace(/(.{3})(.{3})/, '$1 $2');
        apiZip = compact.replace(/(.{3})(.{3})/, '$1-$2');
      } else {
        displayZip = apiZip;
      }
      setQuery(displayZip);
      fetch(`/jm-qq/zipcode-by-city-state?zipcode=${encodeURIComponent(apiZip)}`)
        .then(res => res.json())
        .then(data => {
          let zipObj = null;
          if (Array.isArray(data) && data.length > 0) {
            zipObj = data[0];
          } else if (data && typeof data === 'object' && data.rate) {
            zipObj = data;
          }
          if (zipObj) {
            pendingZipDataRef.current = { zip: displayZip, data: zipObj };
            onZipChange(displayZip, zipObj, true);
            setError('');
            localStorage.setItem('jm-qq-zip', apiZip);
            console.debug('Setting zipcode to: ', apiZip);
            window.dispatchEvent(new CustomEvent('jm-qq-zip-updated', { detail: { zip: displayZip } }));
          } else {
            pendingZipDataRef.current = null;
            onZipChange(displayZip, null, true);
            setError('Please enter a valid US or Canadian zip/postal code.');
          }
        })
        .catch(() => {
          pendingZipDataRef.current = null;
          onZipChange(displayZip, null, true);
          setError('Apologies, we\'re having trouble looking up that zip/postal code. Please try again later.');
        });
    } else if (zip.length > 0) {
      setQuery(zip);
      pendingZipDataRef.current = null;
      onZipChange(zip, null, true);
      setError('Please enter a valid US or Canadian zip/postal code.');
    } else {
      setQuery(zip);
      pendingZipDataRef.current = null;
      onZipChange(zip, null, true);
      setError('');
    }
  };


  return (
    <div className="w-full">
      <label htmlFor="zipCode" className="qqOptionsLabel block text-step-00 text-left font-light text-gray-700">
        Zip/Postal Code
      </label>
      <input
        type="text"
        id="zipCode"
        className={`jewelryOptionsZipInput mp-no-track rounded-sm! bg-cloud! border border-border! text-step-1${error ? ' qqInputError' : ''}`}
        value={query}
        onChange={e => handleZipChange(e.target.value)}
        placeholder="Zip or Postal Code"
        aria-label="Zip or Postal Code"
      />
      {error && (
        <p className="text-step-00 qqFormError" role="alert">{error}</p>
      )}
    </div>
  );
};

const JewelryTypeSelect = ({ onSelect, jewelryTypes }) => {
  const types = jewelryTypes && jewelryTypes.length > 0 ? jewelryTypes : [];
  const [selectedType, setSelectedType] = useState(types[0]);

  const handleChange = (e) => {
    const selected = e.target.value;
    setSelectedType(selected);
    if (onSelect) onSelect(selected);
  };

  useEffect(() => {
    if (!types.includes(selectedType)) {
      setSelectedType(types[0]);
      if (onSelect) onSelect(types[0]);
    }
  }, [onSelect, types, selectedType]);

  return (
    <div>
      <label htmlFor="jewelryOptions" className="qqOptionsLabel block text-step-00 text-left font-light text-gray-700">
        Insure my
      </label>
      <select
        id="jewelryOptions"
        name="options"
        className="jewelryOptionsSelect mp-no-track rounded-sm! bg-cloud! border border-border! text-step-1"
        value={selectedType}
        onChange={handleChange}
      >
        {types.map((type, index) => (
          <option key={index} value={type}>{type}</option>
        ))}
      </select>
    </div>
  );
};

const ValueSelect = ({ value, onSliderChange, defaultValue }) => {
  // Use the defaultValue prop passed from parent, fallback to value if not provided
  const fallbackValue = defaultValue !== undefined ? defaultValue : value;

  const handleInputChange = (event) => {
    const input = event.target.value;
    if (input === '' || isNaN(Number(input)) || Number(input) < 1) {
      onSliderChange({ target: { value: '' } });
      return;
    }
    let newValue = Number(input);
    if (newValue > 100000) newValue = 100000;
    onSliderChange({ target: { value: newValue } });
  };

  const handleInputBlur = () => {
    // Reset to default value if empty on blur
    if (value === '') {
      onSliderChange({ target: { value: fallbackValue } });
    }
  };

  return (
    <div className="w-full">
      <label htmlFor="jewelryValue" className="qqOptionsLabel block text-step-00 text-left font-light text-gray-700">
        Estimated Value
      </label>
      <div className="relative">
        <span className="absolute top-[25px] left-[12px]">$</span>
        <input
          id="jewelryValue"
          type="number"
          min="1"
          max="100000"
          inputMode="numeric"
          pattern="[0-9]*"
          className="qqOptionsInput mp-no-track rounded-sm! bg-cloud! border border-border! text-step-1 pl-[22px]"
          value={value === '' ? '' : value}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
        />
      </div>
    </div>
  );
};

/**
 * Sanitize a URL string read from the DOM before using it for navigation.
 * Only allows http and https schemes; returns null if invalid or unsafe.
 */
function sanitizeUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') {
    return null;
  }
  try {
    // Use the current origin as base so relative URLs are supported.
    const url = new URL(rawUrl, window.location.origin);
    const protocol = url.protocol.toLowerCase();
    if (protocol === 'http:' || protocol === 'https:') {
      return url.toString();
    }
    // Disallow non-http(s) protocols such as javascript:, data:, etc.
    return null;
  } catch (e) {
    // Malformed URL
    return null;
  }
}

const SubmitButton = ({ value, zip, jewelryType, disclaimerText, submitButtonText, qqLink, qqDesktopMode }) => {
  const handleClick = (e) => {
    e.preventDefault();
    let paramsObj = {
      zipcode: zip.replace('-',''),
      jewelryType: jewelryType,
      jewelryValue: value,
    };
    if (jewelryType === 'Engagement Ring') {
      paramsObj.jewelryType = 'ring';
      paramsObj.jewelryDescription = 'lady|engagement';
    }
    if (typeof paramsObj.jewelryType === 'string') {
      paramsObj.jewelryType = paramsObj.jewelryType.toLowerCase();
    }

    const params = new URLSearchParams(paramsObj);
    const envLinkMap = new Map([
      ['test', 'https://dsqa.dop.test.core.jminsure.com/direct'],
      ['stage', 'https://stage.quote.jewelersmutual.com/direct'],
      ['dev', 'https://dsqa.dop.dev.core.jminsure.com/direct'],
      ['qa', 'https://dsqa.dop.test.core.jminsure.com/direct'],
      ['ode15', 'https://dsqa.dop.test.core.jminsure.com/direct'],
      ['prod', 'https://quote.jewelersmutual.com/direct'],
    ]);
    const currentEnv = window?.drupalSettings?.environment_name;
    // if qq link is provided via drupal, use it. Otherwise, determine link based on env with ode fallback
    const sanitizedQqLink = sanitizeUrl(qqLink);
    if (sanitizedQqLink) {
      window.location.href = sanitizedQqLink;
      return;
    }
    
    //all ode envs redirect to link mapped to ode15
    const updatedEnv = currentEnv.slice(0, 3) === 'ode' ? 'ode15' : currentEnv;
    let envLink = 'https://quote.jewelersmutual.com/direct';
    if (currentEnv && envLinkMap.has(updatedEnv)) {
      envLink = envLinkMap.get(updatedEnv);
    }

    let targetUrl = `${envLink}?${params.toString()}`;
    let buttonText = (e.target.innerHTML !== undefined && e.target.innerHTML !== null) ? e.target.innerHTML.toString() : '';

    if (typeof window.mixpanel !== 'undefined' && typeof window.mixpanel.track === 'function') {
      window.mixpanel.track('Link Clicked', {
        'Link text': buttonText,
        'Link URL': targetUrl,
        'Current Page Name': document.title,
      }, { send_immediately: true });
    }
    window.location.href = targetUrl;
  };
  const buttonText = (jewelryType === 'Ring' || jewelryType === 'Engagement Ring')
    ? (submitButtonText || 'Check your rate >')
    : 'Continue';
  return (
    <div className="w-full">
      <button
        id="qqSubmitButton"
        className="w-full mb-s px-md py-xs bg-accent border border-accent hover:bg-white hover:text-accent rounded-sm text-white cursor-pointer transition-colors duration-150 ease-out"
        type="button"
        onClick={handleClick}
      >
        {buttonText}
      </button>
      <p className={`text-step-00 text-brand ${qqDesktopMode === "On" ? "text-left els:text-center" : "text-left"}`}>
        <span className="disclaimer">{disclaimerText}</span>
      </p>
    </div>
  );
};

const QuickQuoteWidget = ({
  value: initialValue = 5000,
  disclaimerText,
  headingText,
  submitButtonText,
  costAltText,
  jewelryTypes,
  qqLink,
  qqDesktopMode,
}) => {
  const storedZip = typeof window !== 'undefined' ? localStorage.getItem('jm-qq-zip') : null;
  const initialZip = storedZip || '';
  const [value, setValue] = useState(initialValue);
  const [selectedZip, setSelectedZip] = useState(initialZip);
  const [zipData, setZipData] = useState(null);
  const [selectedJewelryType, setSelectedJewelryType] = useState('Engagement Ring');
  const [isLoading, setIsLoading] = useState(false);
  const [zipError, setZipError] = useState('');
  const [isZipLoading, setIsZipLoading] = useState(false);
  const [showCostAltLoading, setShowCostAltLoading] = useState(false);
  const [zipLookupComplete, setZipLookupComplete] = useState(false);
  const [items, setItems] = useState([]);
  const [dynamicJewelryTypes, setDynamicJewelryTypes] = useState([]);
  const [currentAltText, setCurrentAltText] = useState('');
  const [currentDisclaimer, setCurrentDisclaimer] = useState('');

  // Per-mount only; not persisted or shared across instances.
  const itemIdRef = useRef(null);
  if (itemIdRef.current === null) {
    itemIdRef.current = generateItemId();
  }

  // Mirrors selectedZip but updated synchronously at the same time as setSelectedZip,
  // so the jm-qq-zip-updated listener below can tell its own update apart from a
  // genuinely different instance's update without racing React's render cycle.
  const selectedZipRef = useRef(initialZip);

  useEffect(() => {
    fetch('/jm-qq/quick-quote-items')
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to fetch item types');
        }
        return response.json();
      })
      .then((data) => {
        setItems(data);
        let types = Array.from(new Set(data.map(item => item.item_type).filter(Boolean)));
        const engagementIdx = types.indexOf('Engagement Ring');
        if (engagementIdx > 0) {
          types = [types[engagementIdx], ...types.slice(0, engagementIdx), ...types.slice(engagementIdx + 1)];
        }
        setDynamicJewelryTypes(types);
        if (types.length > 0) {
          setSelectedJewelryType(types[0]);
          const found = data.find(item => item.item_type === types[0]);
          setCurrentAltText(found ? found.item_alt_text : '');
          setCurrentDisclaimer(found ? found.item_disclaimer : '');
        }
      })
      .catch((error) => {
        console.error('Error fetching item types:', error);
      });
  }, []);

  useEffect(() => {
    const found = items.find(item => item.item_type === selectedJewelryType);
    setCurrentAltText(found ? found.item_alt_text : '');
    setCurrentDisclaimer(found ? found.item_disclaimer : '');
  }, [selectedJewelryType, items]);

  // Canadian postal code normalization helper
  function normalizeZip(zip) {
    if (!zip) return '';
    let z = zip.toUpperCase().replace(/\s+/g, '');
    // Only add dash if not already present in the correct position
    if (CA_POSTAL_REGEX.test(z)) {
      if (!/^.{3}-.{3}$/.test(z)) {
        z = z.replace(/(.{3})(.{3})/, '$1-$2');
      }
    }
    return z;
  }

  const performZipLookup = (zipToLookup) => {
    let normalizedZip = normalizeZip(zipToLookup);
    setIsZipLoading(true);
    setZipLookupComplete(false);
    fetch(`/jm-qq/zipcode-by-city-state?zipcode=${encodeURIComponent(normalizedZip)}`)
      .then(res => res.json())
      .then(data => {
        let zipObj = null;
        let apiZip = normalizedZip;
        if (Array.isArray(data) && data.length > 0) {
          zipObj = data[0];
          if (zipObj.zipcode) apiZip = zipObj.zipcode;
        } else if (data && typeof data === 'object' && data.rate) {
          zipObj = data;
          if (zipObj.zipcode) apiZip = zipObj.zipcode;
        }
        setZipData(zipObj);
        selectedZipRef.current = apiZip;
        setSelectedZip(apiZip);
        localStorage.setItem('jm-qq-zip', apiZip);
        console.debug('Setting zipcode to: ', apiZip);
        window.dispatchEvent(new CustomEvent('jm-qq-zip-updated', { detail: { zip: apiZip } }));
        setIsZipLoading(false);
        setZipLookupComplete(true);
      })
      .catch(() => {
        setZipData(null);
        setIsZipLoading(false);
        setZipLookupComplete(true);
        localStorage.setItem('jm-qq-zip', '');
      });
  };

  useEffect(() => {
    if (zipData) return;
    if (initialZip) {
      performZipLookup(initialZip);
      return;
    }
    // No stored zip - fetch the live, uncached CF-derived postal code for
    // this specific request rather than trusting a value baked into
    // cacheable HTML (which can replay a stale/unrelated visitor's location).
    setIsZipLoading(true);
    fetch('/jm-qq/geo-postal-code')
      .then(res => res.json())
      .then(data => {
        const candidate = data && data.postal_code;
        performZipLookup(candidate || pickDefaultZip());
      })
      .catch(() => {
        performZipLookup(pickDefaultZip());
      });
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const newZip = (e && e.detail && e.detail.zip) || localStorage.getItem('jm-qq-zip');
      // Compare against the ref, not the selectedZip closure: the ref is updated
      // synchronously wherever this instance sets its own zip, so it's already
      // current by the time this instance's own dispatch reaches this handler.
      // The state closure would still be stale at that point (React hasn't
      // re-rendered yet), making a self-triggered update look like it came from
      // a different widget instance and firing a redundant duplicate fetch.
      if (newZip && newZip !== selectedZipRef.current) {
        let normalizedZip = normalizeZip(newZip);
        setIsZipLoading(true);
        fetch(`/jm-qq/zipcode-by-city-state?zipcode=${encodeURIComponent(normalizedZip)}`)
          .then(res => res.json())
          .then(data => {
            let zipObj = null;
            if (Array.isArray(data) && data.length > 0) {
              zipObj = data[0];
            } else if (data && typeof data === 'object' && data.rate) {
              zipObj = data;
            }
            selectedZipRef.current = newZip;
            setZipData(zipObj);
            setSelectedZip(newZip);
            setIsZipLoading(false);
          })
          .catch(() => {
            setZipData(null);
            setIsZipLoading(false);
          });
      }
    };
    window.addEventListener('jm-qq-zip-updated', handler);
    return () => window.removeEventListener('jm-qq-zip-updated', handler);
  }, []);

  // Calculate minimum premium
  let polMinPrem = getPolMinPremForState(zipData, selectedZip);

  const handleZipChange = (zip, data, isUserInput = false) => {
    if (data) {
      zip = data.county == 'Canada' && data.zipcode ? data.zipcode : zip;
    }
    console.log(zip);
    if (isUserInput) setIsZipLoading(true);
    selectedZipRef.current = zip;
    setSelectedZip(zip);
    setZipData(data);
  };

  useEffect(() => {
    if (isZipLoading) {
      setIsZipLoading(false);
    }
  }, [zipData]);

  let rate = null;
  let minPremiumOverride = null;

  if (zipData && zipData.rate) {
    const t1 = zipData.rate.t1;
    const t2 = zipData.rate.t2;
    const t3 = zipData.rate.t3;
    if (typeof value === 'number' && value >= 1 && value <= 100000) {
      if (value < 15000 && t1 && !isNaN(Number(t1))) {
        rate = Number(t1) * 0.01;
      } else if (value <= 30000 && t2 && !isNaN(Number(t2))) {
        rate = Number(t2) * 0.01;
      } else if (t3 && !isNaN(Number(t3))) {
        rate = Number(t3) * 0.01;
      }
      const override = zipData.rate.min_premium_override;
      if (override !== undefined && override !== null && !isNaN(Number(override)) && Number(override) > 0) {
        minPremiumOverride = Number(override);
      } else if (CA_POSTAL_REGEX.test(selectedZip)) {
        minPremiumOverride = 65;
      } else {
        minPremiumOverride = 60;
      }
    }
  }

  let yearlyCost = (rate && typeof value === 'number' && value >= 1 && value <= 100000) ? Math.round(value * rate) : null;
  const minPremium = minPremiumOverride !== null ? minPremiumOverride : polMinPrem;
  if (yearlyCost !== null && minPremium > yearlyCost) {
    yearlyCost = minPremium;
  }
  let monthlyTotalCost = (yearlyCost && yearlyCost > 0) ? Math.ceil(yearlyCost / 12) : null;

  const [animatedMonthlyCost, setAnimatedMonthlyCost] = useState(monthlyTotalCost);
  const prevMonthlyCostRef = useRef(monthlyTotalCost);

  const lastZipRef = useRef(selectedZip);
  const lastMonthlyCostRef = useRef(monthlyTotalCost);

  // Track initial load to skip firing Quote Updated on page load
  const isInitialLoadRef = useRef(true);

  // Track last tracked result to avoid duplicate Quote Updated fires
  const lastTrackedResultRef = useRef({
    yearlyCost: null,
    zipCode: null,
    jewelryType: null,
    itemValue: null,
  });

  useEffect(() => {
    if (isZipLoading || zipError || monthlyTotalCost == null) return;
    let shouldAnimate = false;
    if (monthlyTotalCost !== lastMonthlyCostRef.current) {
      shouldAnimate = true;
    } else if (selectedZip !== lastZipRef.current) {
      shouldAnimate = true;
    }
    lastMonthlyCostRef.current = monthlyTotalCost;
    lastZipRef.current = selectedZip;
    if (!shouldAnimate) {
      // A second zipData update (e.g. cross-instance zip sync landing right
      // after this instance's own lookup) re-runs this effect and cancels the
      // in-flight timer below via cleanup. The refs above already match by
      // then, so there's nothing left to animate to — but isLoading must still
      // resolve here, or it's stuck true forever with no timer left to clear
      // it. Likewise, force animatedMonthlyCost/prevMonthlyCostRef to the
      // current value in case the cancelled timer never got to set them.
      setIsLoading(false);
      setAnimatedMonthlyCost(monthlyTotalCost);
      prevMonthlyCostRef.current = monthlyTotalCost;
      return;
    }
    setIsLoading(true);
    const timer = setTimeout(() => {
      setAnimatedMonthlyCost(monthlyTotalCost);
      prevMonthlyCostRef.current = monthlyTotalCost;
      setIsLoading(false);

      // Fire Quote Updated after animation completes (when user sees the result)
      // Skip initial page load, only fire for Ring/Engagement Ring types here
      if (selectedJewelryType === 'Engagement Ring' || selectedJewelryType === 'Ring') {
        if (isInitialLoadRef.current) {
          // Mark initial load complete and store initial values without firing
          isInitialLoadRef.current = false;
          lastTrackedResultRef.current = {
            yearlyCost: yearlyCost,
            zipCode: selectedZip,
            jewelryType: selectedJewelryType,
            itemValue: value,
          };
        } else {
          // Check if result actually changed from last tracked
          const lastResult = lastTrackedResultRef.current;
          const isNewResult =
            yearlyCost !== lastResult.yearlyCost ||
            selectedZip !== lastResult.zipCode ||
            selectedJewelryType !== lastResult.jewelryType ||
            value !== lastResult.itemValue;

          if (isNewResult) {
            // Update tracking ref
            lastTrackedResultRef.current = {
              yearlyCost: yearlyCost,
              zipCode: selectedZip,
              jewelryType: selectedJewelryType,
              itemValue: value,
            };

            fireQuoteUpdatedTrackers({
              jewelryType: selectedJewelryType,
              itemValue: value,
              zipCode: selectedZip,
              zipData: zipData,
              yearlyCost: yearlyCost,
              itemId: itemIdRef.current,
              previous: lastResult,
            });
          }
        }
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [monthlyTotalCost, selectedZip, isZipLoading, zipError, selectedJewelryType, value, yearlyCost, zipData]);

  useEffect(() => {
    if (
      selectedJewelryType !== 'Engagement Ring' &&
      selectedJewelryType !== 'Ring' &&
      costAltText &&
      costAltText !== DEFAULT_COST_ALT_TEXT
    ) {
      setShowCostAltLoading(true);
      const timer = setTimeout(() => {
        setShowCostAltLoading(false);

        // Fire Quote Updated after animation completes for non-ring types
        // Only fire if rate is valid (yearlyCost exists)
        if (yearlyCost !== null && !zipError && zipLookupComplete) {
          if (isInitialLoadRef.current) {
            // Mark initial load complete and store initial values without firing
            isInitialLoadRef.current = false;
            lastTrackedResultRef.current = {
              yearlyCost: yearlyCost,
              zipCode: selectedZip,
              jewelryType: selectedJewelryType,
              itemValue: value,
            };
          } else {
            // Check if result actually changed from last tracked
            const lastResult = lastTrackedResultRef.current;
            const isNewResult =
              yearlyCost !== lastResult.yearlyCost ||
              selectedZip !== lastResult.zipCode ||
              selectedJewelryType !== lastResult.jewelryType ||
              value !== lastResult.itemValue;

            if (isNewResult) {
              // Update tracking ref
              lastTrackedResultRef.current = {
                yearlyCost: yearlyCost,
                zipCode: selectedZip,
                jewelryType: selectedJewelryType,
                itemValue: value,
              };

              fireQuoteUpdatedTrackers({
                jewelryType: selectedJewelryType,
                itemValue: value,
                zipCode: selectedZip,
                zipData: zipData,
                yearlyCost: yearlyCost,
                itemId: itemIdRef.current,
                previous: lastResult,
              });
            }
          }
        }
      }, 800);
      return () => clearTimeout(timer);
    } else {
      setShowCostAltLoading(false);
    }
  }, [selectedJewelryType, costAltText, yearlyCost, zipError, zipLookupComplete, selectedZip, value, zipData]);

  // Wrap setSelectedJewelryType to set loading before changing type
  const handleJewelryTypeSelect = (type) => {
    setShowCostAltLoading(true);
    setSelectedJewelryType(type);
  };

  // Wrap setValue to set loading before changing value
  const handleValueChange = (e) => {
    const input = e.target.value;
    setIsLoading(true);
    if (input === '' || isNaN(Number(input)) || Number(input) < 1) {
      setValue('');
    } else {
      setValue(Number(input));
    }
    setTimeout(() => setIsLoading(false), 800);
  };

  return (
    <div className="flex items-center justify-center">
      <div id="quickQuoteContainer" className={`component-tracking w-full p-m bg-white ${qqDesktopMode === 'On' ? "rounded-t-lg els:rounded-lg" : "rounded-t-lg"} `}>
        <div className= {`flex flex-col ${qqDesktopMode === "On" ? "sm:max-els:max-w-screen-xs" : "align-center max-w-screen-xs"} `}>
          <p className="text-step-2 text-brand" role="heading" aria-level="2">
            {headingText || 'Estimate Your Rate'}
          </p>
          <div className= {`flex ${qqDesktopMode === 'On' ? "flex-col els:flex-row els:gap-s" : "flex-col"} `}>
            <div className="w-full bg-white flex flex-col gap-s mb-s">
              <JewelryTypeSelect
                onSelect={handleJewelryTypeSelect}
                jewelryTypes={dynamicJewelryTypes.length > 0 ? dynamicJewelryTypes : []}
              />
              <ValueSelect
                value={value}
                onSliderChange={handleValueChange}
                defaultValue={initialValue}
              />
              <CitySelect
                onZipChange={handleZipChange}
                error={zipError}
                setError={setZipError}
                setIsZipLoading={setIsZipLoading}
                selectedZip={selectedZip}
              />
            </div>
            <div id="qqRateBox" className={`w-full mb-s p-m rounded-md bg-cloud-mod-1 ${qqDesktopMode === 'On' ? "" : "h-[160px]"} `}>
              {isZipLoading || isLoading || showCostAltLoading ? (
                <div className="h-full flex justify-center items-center text-lg text-brand">
                  <svg className="animate-spin" width="34" height="33" viewBox="0 0 34 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.83534 2.22522C4.86812 4.81409 1.47659 10.0109 1.47659 15.9999C1.47659 24.5732 8.42666 31.5234 17 31.5234C25.5734 31.5234 32.5234 24.5732 32.5234 15.9999C32.5234 10.0109 29.1319 4.81409 24.1646 2.22522" stroke="#05254E" strokeWidth="2.71" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              ) : zipLookupComplete && rate && yearlyCost != null && (selectedJewelryType === 'Engagement Ring' || selectedJewelryType === 'Ring') ? (
                <div className="flex flex-col justify-center items-center h-full">
                  <span className="text-lg text-center text-brand">as low as</span>
                  <span
                    className={'value-display text-center text-brand text-lh-1 ' + (animatedMonthlyCost >= 100000 ? 'text-step-4' : 'text-step-5')}
                  >
                    <span className="superscriptDollar">$</span>{animatedMonthlyCost}<span className="subscriptMonth">/mo</span>
                  </span>
                  <span className="text-lg text-center text-brand">or ${yearlyCost} per year</span>
                </div>
              ) : zipLookupComplete && rate && yearlyCost != null ? (
                currentAltText && currentAltText !== DEFAULT_COST_ALT_TEXT ? <div className="flex-row items-center w-full h-full" dangerouslySetInnerHTML={{ __html: currentAltText }} /> : null
              ) : zipLookupComplete && (!rate || yearlyCost == null) ? (
                <div className="h-full flex justify-center items-center text-lg text-brand">Unable to load rate for this zip code.</div>
              ) : null}
            </div>
          </div>
          <SubmitButton
            value={value}
            zip={selectedZip}
            jewelryType={selectedJewelryType}
            disclaimerText={<span dangerouslySetInnerHTML={{ __html: currentDisclaimer }} />}
            submitButtonText={submitButtonText}
            qqLink={qqLink}
            qqDesktopMode={qqDesktopMode}
          />
        </div>
      </div>
    </div>
  );
};

export default QuickQuoteWidget;
